import React, { useState, useEffect, useCallback } from "react";
import { DefaultButton, PrimaryButton, Link } from "@fluentui/react";
import { getCookieConsentBannerStyles } from "./CookieConsentBanner.styles";

export interface ICookieConsentBannerProps {
  /** Custom message text to display */
  message?: string;
  /** URL for the privacy policy link */
  privacyPolicyUrl?: string;
  /** Text for the privacy policy link */
  privacyPolicyText?: string;
  /** Text for the accept button */
  acceptButtonText?: string;
  /** Text for the decline button */
  declineButtonText?: string;
  /** Local storage key for consent state */
  storageKey?: string;
  /** Auto-hide timeout in milliseconds (optional) */
  autoHideTimeout?: number;
  /** Callback when consent is accepted */
  onAccept?: () => void;
  /** Callback when consent is declined */
  onDecline?: () => void;
}

export const CookieConsentBanner: React.FC<ICookieConsentBannerProps> = ({
  message = "We use cookies to improve your experience and for analytics. Read our",
  privacyPolicyUrl = "https://go.microsoft.com/fwlink/?LinkId=518021",
  privacyPolicyText = "Microsoft Data Privacy Notice",
  acceptButtonText = "Accept All",
  declineButtonText = "Reject All",
  storageKey = "clarityConsent",
  autoHideTimeout,
  onAccept,
  onDecline,
}) => {
  const [visible, setVisible] = useState(false);
  const styles = getCookieConsentBannerStyles();

  useEffect(() => {
    // Show banner only if user hasn't already given consent
    const consent = localStorage.getItem(storageKey);
    if (consent === null) {
      setVisible(true);
    }
  }, [storageKey]);

  const handleAccept = useCallback(() => {
    localStorage.setItem(storageKey, "true");
    window.dispatchEvent(new Event("clarityConsentGranted"));
    setVisible(false);
    onAccept?.();
  }, [storageKey, onAccept]);

  const handleDecline = useCallback(() => {
    localStorage.setItem(storageKey, "false");
    window.dispatchEvent(new Event("clarityConsentDeclined"));
    setVisible(false);
    onDecline?.();
  }, [storageKey, onDecline]);

  useEffect(() => {
    if (autoHideTimeout && visible) {
      const timer = setTimeout(() => {
        setVisible(false);
      }, autoHideTimeout);
      return () => clearTimeout(timer);
    }
  }, [autoHideTimeout, visible]);

  if (!visible) return null;

  return (
    <div
      className={styles.root}
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent banner"
    >
      <div className={styles.content}>
        <p className={styles.text}>
          {message}{" "}
          <Link
            href={privacyPolicyUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Read our ${privacyPolicyText} (opens in new tab)`}
          >
            {privacyPolicyText}
          </Link>
          .
        </p>
      </div>
      <div className={styles.buttonContainer}>
        <PrimaryButton
          text={acceptButtonText}
          onClick={handleAccept}
          aria-label={acceptButtonText}
        />
        <DefaultButton
          text={declineButtonText}
          onClick={handleDecline}
          aria-label={declineButtonText}
        />
      </div>
    </div>
  );
};

export default CookieConsentBanner;
